import {
  Bell,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Copy,
  DollarSign,
  Download,
  Eye,
  EyeOff,
  Layers,
  Mail,
  MessageSquare,
  Plus,
  Save,
  Send,
  Sparkles,
  ThumbsDown,
  Trash2,
  X,
} from "lucide-react";
import { type DragEvent, type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiError, apiBase } from "@/api/client";
import {
  useApplyTemplateToQuote,
  useClients,
  useJob,
  useJobTemplates,
  useMarkDepositReceived,
  usePostQuoteComment,
  usePricingRules,
  usePromoteCommentAttachmentToJob,
  useQuote,
  useQuoteComments,
  useMintShareLink,
  useSendQuote,
  useSendQuoteReminderNow,
  useSuggestQuoteAdditions,
  useUpdateQuote,
} from "@/api/hooks";
import type {
  ClientResponse,
  LineItemConfidence,
  LineItemKind,
  LineItemPayload,
  LineItemSection,
  LineItemSuggestion,
  PricingRuleResponse,
  QuoteCommentAttachmentResponse,
  QuoteCommentResponse,
  QuoteRenderMode,
  QuoteResponse,
  QuoteStatusValue,
} from "@/api/types";
import { CommentThread } from "@/components/CommentThread";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { HelpPopover } from "@/components/HelpPopover";
import { ProjectLedger } from "@/components/ProjectLedger";
import { QuoteStatusBar } from "@/components/QuoteStatusBar";
import { filenameWithoutExtension } from "@/lib/attachments";
import { formatCents, formatRelative, formatTime, shortId } from "@/lib/format";

const STATUS_FLOW: { value: QuoteStatusValue; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "ready", label: "Ready to send" },
  { value: "sent", label: "Sent" },
  { value: "accepted", label: "Accepted" },
  { value: "declined", label: "Declined" },
  { value: "expired", label: "Expired" },
];

interface DraftLine extends LineItemPayload {
  /** Stable key for React render identity (server id, or generated). */
  _key: string;
}

/** "Send via Text" — the iMessage handoff. Mints a fresh share
 *  link from the API, then opens an ``sms:`` deep-link with the
 *  client's number + a pre-composed message body. The contractor
 *  reviews + sends the text from their own phone number, which
 *  means: zero per-message cost, zero A2P 10DLC paperwork, zero
 *  carrier filtering risk (it's a person-to-person text from a
 *  known number, not application-to-person traffic).
 *
 *  Disabled when no client phone is on file — the contractor
 *  needs the number on the linked Client record before this works.
 *
 *  iOS Messages handles ``sms:phone?body=...`` natively. Android
 *  default SMS apps mostly do too, but quirks vary by OEM —
 *  worst case the body comes through empty and the contractor
 *  pastes from their clipboard (we copy as a fallback). */
function SendViaTextButton({
  quoteId,
  clientPhone,
}: {
  quoteId: string;
  clientPhone: string | null;
}) {
  const mintShareLink = useMintShareLink(quoteId);
  const [copying, setCopying] = useState<"idle" | "copied">("idle");

  async function onClick() {
    try {
      const result = await mintShareLink.mutateAsync();
      const phoneDigits = (clientPhone ?? "").replace(/[^\d+]/g, "");
      // Construct the sms: URL. We always copy the body to clipboard
      // as a fallback so a contractor on a desktop browser (or an
      // Android SMS app that drops the body param) can paste.
      const encoded = encodeURIComponent(result.suggested_message);
      const sms_url = phoneDigits
        ? `sms:${phoneDigits}?&body=${encoded}` // ``?&`` handles iOS quirks
        : `sms:?&body=${encoded}`;

      // Copy as fallback. Best-effort; some browsers gate clipboard
      // behind permissions, in which case we silently skip.
      try {
        await navigator.clipboard.writeText(result.suggested_message);
        setCopying("copied");
        setTimeout(() => setCopying("idle"), 2000);
      } catch {
        /* clipboard not available — sms: link still works */
      }

      window.location.href = sms_url;
    } catch {
      // Surfaced via mintShareLink.error below; no-op here.
    }
  }

  const disabled = mintShareLink.isPending || !clientPhone;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={
        clientPhone
          ? "Open Messages with the share link pre-loaded — you send from your own phone"
          : "Add a phone number to the client record to enable text send"
      }
      className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite transition hover:bg-pencil-yellow-soft disabled:opacity-50"
    >
      <MessageSquare className="h-3.5 w-3.5" aria-hidden />
      {mintShareLink.isPending
        ? "Preparing…"
        : copying === "copied"
          ? "Copied + opening…"
          : "Send via Text"}
    </button>
  );
}

/** Three distinct PDF surfaces, exposed as a dropdown so the
 *  contractor can tell them apart:
 *    - "Customer PDF": homeowner-facing print copy (no internal
 *      lines, no markup breakdown).
 *    - "Internal PDF": contractor's audit copy with hidden lines
 *      + the markup row in totals.
 *  The "Customer view" surface (the SPA preview link) lives on its
 *  own button next to this group — it's a different medium (web
 *  page vs. file). */
function PdfButtonGroup({ quoteId }: { quoteId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite transition hover:bg-pencil-yellow-soft"
        title="Download as PDF"
      >
        <Download className="h-3.5 w-3.5" aria-hidden />
        PDF
        <ChevronDown className="h-3 w-3" aria-hidden />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-30 mt-1 w-72 rounded-lg border border-rule bg-paper p-2 shadow-lg"
          onMouseLeave={() => setOpen(false)}
        >
          <a
            href={`${apiBase}/quotes/${quoteId}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="block rounded px-2 py-2 text-sm text-graphite hover:bg-paper-warm"
          >
            <span className="font-mono text-sm font-semibold">Customer PDF</span>
            <p className="mt-0.5 font-mono text-[11px] text-graphite-soft">
              Homeowner-facing copy. No hidden lines, no markup.
            </p>
          </a>
          <a
            href={`${apiBase}/quotes/${quoteId}/pdf?include_hidden=true`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="block rounded px-2 py-2 text-sm text-graphite hover:bg-paper-warm"
          >
            <span className="font-mono text-sm font-semibold">Internal PDF</span>
            <p className="mt-0.5 font-mono text-[11px] text-graphite-soft">
              Audit copy with hidden lines + markup breakdown.
            </p>
          </a>
        </div>
      )}
    </div>
  );
}

/** Phone + iPad-portrait card view for one line item. Touch-
 *  friendly version of the desktop row: bigger inputs, stacked
 *  fields, kind/section/room only shown when populated. Drag-and-
 *  drop is omitted on touch (the up/down arrows + select-checkbox
 *  cover ordering + bulk edit). */
function CardLineItem({
  li,
  idx,
  total,
  selected,
  onToggleSelected,
  onPatch,
  onMoveUp,
  onMoveDown,
  onRemove,
  showRoom,
  showSection,
}: {
  li: DraftLine;
  idx: number;
  total: number;
  selected: boolean;
  onToggleSelected: () => void;
  onPatch: (patch: Partial<DraftLine>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  showRoom: boolean;
  showSection: boolean;
}) {
  const lineTotal = li.quantity * li.unit_price_cents;
  const isTbd = (li.kind ?? "firm") === "tbd";
  return (
    <li
      className={
        selected
          ? "rounded-lg border-2 border-graphite bg-pencil-yellow-soft p-3 space-y-3"
          : "rounded-lg border border-rule bg-paper-warm p-3 space-y-3"
      }
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelected}
          aria-label={`Select line ${idx + 1}`}
          className="mt-1 h-4 w-4"
        />
        <div className="min-w-0 flex-1">
          <input
            type="text"
            value={li.description}
            onChange={(e) => onPatch({ description: e.target.value })}
            placeholder="Description"
            className="w-full rounded border border-rule bg-paper px-2 py-2 text-base text-graphite focus:border-graphite focus:outline-none"
          />
          <div className="mt-1 flex flex-wrap items-baseline gap-2">
            <input
              type="text"
              value={li.category}
              onChange={(e) => onPatch({ category: e.target.value })}
              placeholder="category"
              className="rounded border border-rule bg-paper px-2 py-1 font-mono text-xs text-graphite-soft focus:border-graphite focus:outline-none"
            />
            <select
              value={li.kind ?? "firm"}
              onChange={(e) =>
                onPatch({ kind: e.target.value as LineItemKind })
              }
              className="rounded border border-rule bg-paper px-1 py-0.5 font-mono text-[10px] uppercase text-graphite-soft"
            >
              <option value="firm">firm</option>
              <option value="allowance">allow</option>
              <option value="client_supplied">client</option>
              <option value="tbd">tbd</option>
            </select>
            <button
              type="button"
              onClick={() =>
                onPatch({ visible_to_client: !li.visible_to_client })
              }
              title={li.visible_to_client ? "Visible" : "Hidden"}
              className="rounded p-1 text-graphite-soft transition hover:bg-paper hover:text-graphite"
            >
              {li.visible_to_client ? (
                <Eye className="h-4 w-4" aria-hidden />
              ) : (
                <EyeOff className="h-4 w-4 text-burnt" aria-hidden />
              )}
            </button>
          </div>
        </div>
        <span
          className={`flex-none font-mono text-base font-semibold tabular-nums ${
            isTbd ? "text-graphite-soft" : "text-graphite"
          }`}
        >
          {isTbd ? "—" : formatCents(lineTotal)}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft">
            Qty
          </span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={li.quantity}
            onChange={(e) =>
              onPatch({ quantity: Number(e.target.value) || 0 })
            }
            className="mt-0.5 w-full rounded border border-rule bg-paper px-2 py-1.5 text-right font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft">
            Unit
          </span>
          <input
            type="text"
            value={li.unit}
            onChange={(e) => onPatch({ unit: e.target.value })}
            className="mt-0.5 w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft">
            Unit $
          </span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={(li.unit_price_cents / 100).toFixed(2)}
            onChange={(e) =>
              onPatch({
                unit_price_cents: Math.round(
                  (Number(e.target.value) || 0) * 100,
                ),
              })
            }
            className="mt-0.5 w-full rounded border border-rule bg-paper px-2 py-1.5 text-right font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
          />
        </label>
      </div>

      {(showRoom || showSection) && (
        <div className="grid grid-cols-2 gap-2">
          {showRoom && (
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft">
                Room
              </span>
              <input
                type="text"
                list="quote-rooms"
                value={li.room ?? ""}
                onChange={(e) =>
                  onPatch({
                    room: e.target.value.trim() ? e.target.value : null,
                  })
                }
                placeholder="—"
                className="mt-0.5 w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-xs focus:border-graphite focus:outline-none"
              />
            </label>
          )}
          {showSection && (
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft">
                Section
              </span>
              <select
                value={li.section_code ?? ""}
                onChange={(e) =>
                  onPatch({
                    section_code:
                      (e.target.value as DraftLine["section_code"]) || null,
                  })
                }
                className="mt-0.5 w-full rounded border border-rule bg-paper px-1 py-1 font-mono text-xs uppercase"
              >
                <option value="">—</option>
                {(
                  [
                    "a",
                    "b",
                    "c",
                    "d",
                    "e",
                    "f",
                    "g",
                    "h",
                    "i",
                    "j",
                    "k",
                    "l",
                    "m",
                    "n",
                    "o",
                    "p",
                    "q",
                    "r",
                  ] as const
                ).map((s) => (
                  <option key={s} value={s}>
                    {s.toUpperCase()}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {li.assumptions && (
        <p className="text-xs italic text-graphite-soft">{li.assumptions}</p>
      )}

      <div className="flex items-center justify-between border-t border-rule pt-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={idx === 0}
            title="Move up"
            aria-label="Move row up"
            className="rounded p-2 text-graphite-soft transition hover:bg-paper hover:text-graphite disabled:opacity-25"
          >
            <ChevronUp className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={idx === total - 1}
            title="Move down"
            aria-label="Move row down"
            className="rounded p-2 text-graphite-soft transition hover:bg-paper hover:text-graphite disabled:opacity-25"
          >
            <ChevronDown className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <button
          type="button"
          onClick={onRemove}
          title="Remove line"
          className="rounded p-2 text-graphite-soft transition hover:bg-red-pencil/10 hover:text-red-pencil"
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </li>
  );
}

/** Sticky-style toolbar that appears above the line-item table when
 *  one or more rows are selected. Bulk-edit operations: set room,
 *  set section, set kind, toggle visibility, delete. Each action
 *  applies to all selected rows then clears the selection. */
function BulkActionToolbar({
  count,
  onClear,
  onSetRoom,
  onSetSection,
  onSetKind,
  onToggleVisibility,
  onDelete,
}: {
  count: number;
  onClear: () => void;
  onSetRoom: (room: string) => void;
  onSetSection: (section: LineItemSection | "") => void;
  onSetKind: (kind: LineItemKind) => void;
  onToggleVisibility: (visible: boolean) => void;
  onDelete: () => void;
}) {
  const [roomInput, setRoomInput] = useState("");
  return (
    <div className="rounded-lg border-2 border-graphite bg-pencil-yellow-soft p-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-sm font-semibold text-graphite">
          {count} selected
        </span>

        <div className="flex flex-wrap items-center gap-2">
          {/* Set room — type into input, hit Enter to apply. */}
          <input
            type="text"
            value={roomInput}
            onChange={(e) => setRoomInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSetRoom(roomInput.trim());
                setRoomInput("");
              }
            }}
            placeholder="Set room…"
            className="rounded border border-rule bg-paper px-2 py-1 font-mono text-xs"
          />

          {/* Set section */}
          <select
            onChange={(e) => {
              if (e.target.value) {
                onSetSection(e.target.value as LineItemSection);
                e.target.value = "";
              }
            }}
            defaultValue=""
            className="rounded border border-rule bg-paper px-2 py-1 font-mono text-xs uppercase"
            title="Set section"
          >
            <option value="">Section…</option>
            {(
              [
                "a",
                "b",
                "c",
                "d",
                "e",
                "f",
                "g",
                "h",
                "i",
                "j",
                "k",
                "l",
                "m",
                "n",
                "o",
                "p",
                "q",
                "r",
              ] as const
            ).map((s) => (
              <option key={s} value={s}>
                {s.toUpperCase()}
              </option>
            ))}
          </select>

          {/* Set kind */}
          <select
            onChange={(e) => {
              if (e.target.value) {
                onSetKind(e.target.value as LineItemKind);
                e.target.value = "";
              }
            }}
            defaultValue=""
            className="rounded border border-rule bg-paper px-2 py-1 font-mono text-xs"
            title="Set kind"
          >
            <option value="">Kind…</option>
            <option value="firm">firm</option>
            <option value="allowance">allowance</option>
            <option value="client_supplied">client-supplied</option>
            <option value="tbd">tbd</option>
          </select>

          <button
            type="button"
            onClick={() => onToggleVisibility(true)}
            className="rounded border border-rule bg-paper px-2 py-1 font-mono text-xs font-semibold text-graphite hover:bg-pencil-yellow-soft"
          >
            Show all
          </button>
          <button
            type="button"
            onClick={() => onToggleVisibility(false)}
            className="rounded border border-rule bg-paper px-2 py-1 font-mono text-xs font-semibold text-graphite hover:bg-pencil-yellow-soft"
          >
            Hide all
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="rounded border border-red-pencil/40 bg-paper px-2 py-1 font-mono text-xs font-semibold text-red-pencil hover:bg-red-pencil/10"
          >
            Delete
          </button>
        </div>

        <button
          type="button"
          onClick={onClear}
          className="ml-auto font-mono text-xs uppercase tracking-wider text-graphite-soft hover:text-graphite"
        >
          Clear
        </button>
      </div>
    </div>
  );
}

/** Inline icon button that toggles the AI's rationale for a line.
 *  Inline rationale text wastes vertical space on every render;
 *  hiding it behind a [?] icon keeps the table compact and lets
 *  the contractor surface the explanation only when reviewing the
 *  call. When provenance fields are populated, the popover also
 *  shows the verbatim source excerpt + a click-to-jump timestamp
 *  link to the parent walkthrough. */
function AiRationalePopover({
  note,
  sourceExcerpt,
  sourceTimestampSeconds,
  walkthroughId,
}: {
  note: string;
  sourceExcerpt?: string | null;
  sourceTimestampSeconds?: number | null;
  walkthroughId?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const hasTimestamp =
    typeof sourceTimestampSeconds === "number" &&
    sourceTimestampSeconds >= 0 &&
    Boolean(walkthroughId);
  const tsLabel = hasTimestamp ? formatVideoTimestamp(sourceTimestampSeconds!) : null;
  return (
    <div className="relative flex-none">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded border border-pencil-yellow/40 bg-pencil-yellow-soft px-1.5 py-0.5 font-mono text-[10px] font-semibold text-graphite hover:bg-pencil-yellow"
        title="AI rationale for this line"
        aria-expanded={open}
      >
        AI
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-30 mt-1 w-80 rounded border border-rule bg-paper p-3 text-[11px] text-graphite-soft shadow-lg"
          role="dialog"
        >
          <p className="italic">{note}</p>

          {sourceExcerpt && (
            <div className="mt-2 border-l-2 border-pencil-yellow pl-2">
              <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-faint">
                Source
              </span>
              <p className="mt-0.5 italic text-graphite">"{sourceExcerpt}"</p>
            </div>
          )}

          {hasTimestamp && (
            <Link
              to={`/walkthroughs/${walkthroughId}?t=${sourceTimestampSeconds}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 rounded border border-rule bg-paper-warm px-2 py-1 font-mono text-[10px] font-semibold not-italic text-graphite hover:bg-pencil-yellow-soft"
              onClick={() => setOpen(false)}
            >
              ▶ Jump to {tsLabel} in walkthrough
            </Link>
          )}

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-3 block w-full rounded border border-rule px-2 py-0.5 text-center font-mono text-[10px] font-semibold not-italic text-graphite-soft hover:text-graphite"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}

/** Format `123` (seconds) → `2:03` for click-to-jump labels. Use
 *  `H:MM:SS` once we cross an hour because contractor walkthroughs
 *  can run 30+ minutes. */
function formatVideoTimestamp(totalSeconds: number): string {
  const hh = Math.floor(totalSeconds / 3600);
  const mm = Math.floor((totalSeconds % 3600) / 60);
  const ss = Math.floor(totalSeconds % 60);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`;
}

export function QuoteDetailPage() {
  const { quoteId = "" } = useParams<{ quoteId: string }>();
  const quoteQuery = useQuote(quoteId);
  const update = useUpdateQuote(quoteId);
  const suggest = useSuggestQuoteAdditions(quoteId);
  const send = useSendQuote(quoteId);
  const sendReminder = useSendQuoteReminderNow(quoteId);
  // Pricing rules feed the "pick category to autofill price" UX when
  // adding a line manually. Org is implicit from the caller's session.
  const rulesQuery = usePricingRules();
  // Pull the parent job so we can default the recipient email to its
  // linked client (when one exists).
  const jobQuery = useJob(quoteQuery.data?.job_id);
  // Clients in the quote's org for the Send dialog dropdown.
  // Org-scoped server-side: org users get their own org's clients
  // regardless of the param; pencilout_admin gets the quote's org via
  // the explicit ``organization_id`` filter.
  const clientsQuery = useClients(jobQuery.data?.organization_id ?? null);
  // Comment thread on this quote — polls every 30s so an org user
  // sees client replies arrive without a hard refresh.
  const commentsQuery = useQuoteComments(quoteId);
  const postComment = usePostQuoteComment(quoteId);

  // Local edit state. Initialized once from server data; user edits are
  // tracked here until "Save changes" round-trips a PATCH.
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [markupPct, setMarkupPct] = useState(0);
  const [terms, setTerms] = useState("");
  const [renderMode, setRenderMode] = useState<QuoteRenderMode>("itemized");
  const [useSectionalTemplate, setUseSectionalTemplate] = useState(false);
  // Post-acceptance: collapse the line-item editor to read-only by
  // default. Contractor explicitly opens "Edit line items" to make
  // changes (rare + risky after sign-off). Pre-acceptance, the
  // editor is always expanded — this flag stays false.
  const [editorExpanded, setEditorExpanded] = useState(false);
  const [confirmingStatus, setConfirmingStatus] = useState<QuoteStatusValue | null>(null);
  const [suggestions, setSuggestions] = useState<LineItemSuggestion[] | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [lastSendShareUrl, setLastSendShareUrl] = useState<string | null>(null);
  const [depositOpen, setDepositOpen] = useState(false);
  const markDeposit = useMarkDepositReceived(quoteId);

  // Send-to-job (promote a comment-attachment to the parent job's
  // curated attachments). The dialog opens when a contractor clicks
  // the per-attachment "Send to job" button rendered by CommentThread.
  const [promoteTarget, setPromoteTarget] = useState<{
    comment: QuoteCommentResponse;
    attachment: QuoteCommentAttachmentResponse;
  } | null>(null);
  const [promoteSuccessLabel, setPromoteSuccessLabel] = useState<string | null>(null);
  const promoteAttachment = usePromoteCommentAttachmentToJob(
    quoteId,
    quoteQuery.data?.job_id ?? "",
  );

  // Hydrate edit state on initial arrival of THIS quote's data.
  // Subsequent refetches (30s poll, tab-focus, post-save) intentionally
  // leave local edit state alone — otherwise a polled refetch
  // triggered by the customer accepting would blow away the
  // contractor's unsaved line-item edits. The StatusBar + header read
  // directly from ``quoteQuery.data`` so server-side changes (status,
  // accepted_at, signed_name, view_count) still surface immediately
  // without us touching ``draft`` / ``markupPct`` / ``terms``.
  const hydratedQuoteIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!quoteQuery.data) return;
    if (hydratedQuoteIdRef.current === quoteQuery.data.id) return;
    hydratedQuoteIdRef.current = quoteQuery.data.id;
    setDraft(
      quoteQuery.data.line_items.map((li) => ({
        _key: li.id,
        id: li.id,
        category: li.category,
        description: li.description,
        quantity: li.quantity,
        unit: li.unit,
        unit_price_cents: li.unit_price_cents,
        confidence: li.confidence,
        kind: li.kind ?? "firm",
        assumptions: li.assumptions ?? null,
        room: li.room ?? null,
        section_code: li.section_code ?? null,
        source_excerpt: li.source_excerpt ?? null,
        source_timestamp_seconds: li.source_timestamp_seconds ?? null,
        visible_to_client: li.visible_to_client,
        ai_notes: li.ai_notes,
      })),
    );
    setMarkupPct(quoteQuery.data.markup_pct);
    setTerms(quoteQuery.data.terms ?? "");
    setRenderMode(quoteQuery.data.render_mode);
    setUseSectionalTemplate(quoteQuery.data.use_sectional_template ?? false);
  }, [quoteQuery.data]);

  // Live recompute totals as the user edits. Two distinct totals:
  // - ``customerSubtotal`` / ``customerTotal``: only the lines marked
  //   visible_to_client. This is what the homeowner sees on the
  //   public share view + on the printed PDF + in the email.
  // - ``internalSubtotal`` / ``internalTotal``: every line, including
  //   ones the contractor's hiding from the client. The contractor's
  //   internal view of "what's actually in this job".
  // TBD lines are placeholders with no committed price — they
  // preserve the slot in the ledger but must NOT contribute to the
  // total either internally or on the customer-facing surface
  // (otherwise the contractor's "Customer total" would silently
  // count phantom dollars). Mirrors the server-side rule in
  // ``_line_total_for_kind``.
  const customerSubtotal = useMemo(
    () =>
      draft
        .filter((li) => li.visible_to_client && (li.kind ?? "firm") !== "tbd")
        .reduce((sum, li) => sum + li.quantity * li.unit_price_cents, 0),
    [draft],
  );
  const internalSubtotal = useMemo(
    () =>
      draft
        .filter((li) => (li.kind ?? "firm") !== "tbd")
        .reduce((sum, li) => sum + li.quantity * li.unit_price_cents, 0),
    [draft],
  );
  const customerTotal = Math.ceil(customerSubtotal * (1 + markupPct));
  const internalTotal = Math.ceil(internalSubtotal * (1 + markupPct));
  const hasHiddenLines = customerSubtotal !== internalSubtotal;
  const dirty = useMemo(() => {
    if (!quoteQuery.data) return false;
    if (markupPct !== quoteQuery.data.markup_pct) return true;
    if ((terms || "") !== (quoteQuery.data.terms || "")) return true;
    if (renderMode !== quoteQuery.data.render_mode) return true;
    if (useSectionalTemplate !== (quoteQuery.data.use_sectional_template ?? false))
      return true;
    if (draft.length !== quoteQuery.data.line_items.length) return true;
    for (let i = 0; i < draft.length; i++) {
      const a = draft[i];
      const b = quoteQuery.data.line_items[i];
      if (!a || !b) return true;
      if (
        a.id !== b.id ||
        a.category !== b.category ||
        a.description !== b.description ||
        a.quantity !== b.quantity ||
        a.unit !== b.unit ||
        a.unit_price_cents !== b.unit_price_cents ||
        a.confidence !== b.confidence ||
        (a.kind ?? "firm") !== (b.kind ?? "firm") ||
        (a.assumptions ?? "") !== (b.assumptions ?? "") ||
        (a.room ?? "") !== (b.room ?? "") ||
        (a.section_code ?? null) !== (b.section_code ?? null) ||
        a.visible_to_client !== b.visible_to_client
      )
        return true;
    }
    return false;
  }, [draft, markupPct, terms, renderMode, useSectionalTemplate, quoteQuery.data]);

  // Smart column hiding: drop the Room + Sec columns from the
  // editor table when no line is using them AND the quote isn't on
  // the sectional template. Same for Conf. when every line is high.
  // Trade-off: the columns reappear once any line populates the
  // field, which is the right behavior — once dad starts using
  // them the editor expands automatically.
  const hasRooms = useMemo(
    () => draft.some((li) => Boolean(li.room && li.room.trim())),
    [draft],
  );
  const hasSections = useMemo(
    () => draft.some((li) => Boolean(li.section_code)),
    [draft],
  );
  const showRoomColumn = useSectionalTemplate || hasRooms;
  const showSectionColumn = useSectionalTemplate || hasSections;
  const showConfColumn = useMemo(
    () => draft.some((li) => li.confidence !== "high"),
    [draft],
  );
  // Total visible columns matters for the empty-row colspan. Base
  // 10 (select-checkbox, Category, Description, Qty, Unit, Unit $,
  // Total, Kind, Visible, delete) + 1 for each conditional column.
  const visibleColCount =
    10 +
    (showRoomColumn ? 1 : 0) +
    (showSectionColumn ? 1 : 0) +
    (showConfColumn ? 1 : 0);

  // Bulk-selection state for the line-item table. When any row is
  // selected, a sticky toolbar appears with operations that act on
  // the entire selection: set Room, set Section, toggle Visible,
  // mark client-supplied, delete. Uses _key as the stable id (the
  // server id may not exist for newly-added rows).
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const allSelected = draft.length > 0 && selectedKeys.size === draft.length;
  const someSelected = selectedKeys.size > 0 && !allSelected;
  function toggleRow(key: string) {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }
  function toggleAll() {
    setSelectedKeys((prev) => {
      if (prev.size === draft.length) return new Set();
      return new Set(draft.map((li) => li._key));
    });
  }
  function clearSelection() {
    setSelectedKeys(new Set());
  }

  // Bulk operations: apply a partial to every selected row, then
  // clear the selection. Keep the patch shape compatible with
  // patchDraft so the editor's existing flow handles it.
  function bulkPatch(patch: Partial<DraftLine>) {
    setDraft((prev) =>
      prev.map((row) =>
        selectedKeys.has(row._key) ? { ...row, ...patch } : row,
      ),
    );
    clearSelection();
  }
  function bulkDelete() {
    if (
      selectedKeys.size > 0 &&
      !window.confirm(`Remove ${selectedKeys.size} line item(s)?`)
    ) {
      return;
    }
    setDraft((prev) => prev.filter((row) => !selectedKeys.has(row._key)));
    clearSelection();
  }

  // Manual reorder via per-row up/down arrows. Used on the card
  // view (narrow screens, where touch is the input mode) AND
  // alongside drag-and-drop on the desktop table — drag is faster
  // for big reorderings, arrows are precise + accessible.
  function moveRow(idx: number, direction: -1 | 1) {
    const target = idx + direction;
    if (target < 0 || target >= draft.length) return;
    setDraft((prev) => {
      const next = [...prev];
      const [moved] = next.splice(idx, 1);
      if (moved) next.splice(target, 0, moved);
      return next;
    });
  }

  // Drag-and-drop reorder on the desktop table. The row's
  // ``onDragStart`` captures the source index in a ref; the row's
  // ``onDrop`` reorders the array. Card view doesn't enable drag
  // (touch drag is finicky); the arrow buttons cover that path.
  const dragSourceIdx = useRef<number | null>(null);
  function onRowDragStart(idx: number) {
    dragSourceIdx.current = idx;
  }
  function onRowDragEnd() {
    dragSourceIdx.current = null;
  }
  function onRowDragOver(e: DragEvent) {
    if (dragSourceIdx.current !== null) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    }
  }
  function onRowDrop(targetIdx: number) {
    const src = dragSourceIdx.current;
    dragSourceIdx.current = null;
    if (src === null || src === targetIdx) return;
    setDraft((prev) => {
      const next = [...prev];
      const [moved] = next.splice(src, 1);
      if (moved) {
        // After splice, target index shifts down by 1 if src was
        // before it. Account for that so the item lands where the
        // user expected the cursor to be.
        const adjusted = src < targetIdx ? targetIdx - 1 : targetIdx;
        next.splice(adjusted, 0, moved);
      }
      return next;
    });
  }

  // Datalist suggestions for the line-item category + unit fields.
  // MUST run before any early-return below — React's rules of hooks
  // require a stable hook count across every render, and the early
  // returns don't call them.
  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rulesQuery.data ?? []) set.add(r.category);
    for (const li of draft) if (li.category) set.add(li.category);
    return Array.from(set).sort();
  }, [rulesQuery.data, draft]);
  const unitOptions = useMemo(() => {
    const set = new Set<string>([
      "ea",
      "lf",
      "sqft",
      "sf",
      "lin ft",
      "hr",
      "day",
      "lot",
      "yd",
      "cf",
    ]);
    for (const r of rulesQuery.data ?? []) if (r.unit) set.add(r.unit);
    for (const li of draft) if (li.unit) set.add(li.unit);
    return Array.from(set).sort();
  }, [rulesQuery.data, draft]);

  if (quoteQuery.isLoading) {
    return <p className="text-sm text-graphite-soft">Loading quote…</p>;
  }
  if (quoteQuery.error && !quoteQuery.data) {
    return (
      <p className="text-sm text-red-pencil">
        {quoteQuery.error instanceof ApiError ? quoteQuery.error.detail : "Could not load quote."}
      </p>
    );
  }
  const quote = quoteQuery.data;
  if (!quote) return null;

  function patchDraft(idx: number, patch: Partial<DraftLine>) {
    setDraft((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  }
  function removeDraft(idx: number) {
    setDraft((prev) => prev.filter((_, i) => i !== idx));
  }
  function addDraft(seed?: Partial<DraftLine>) {
    setDraft((prev) => [
      ...prev,
      {
        _key: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        id: null,
        category: "",
        description: "",
        quantity: 1,
        unit: "ea",
        unit_price_cents: 0,
        confidence: "high",
        kind: "firm",
        assumptions: null,
        room: null,
        section_code: null,
        visible_to_client: true,
        ai_notes: null,
        ...seed,
      },
    ]);
  }

  /** Add a line pre-filled from a pricing rule (category + unit +
   * unit_price land from the rule; description + quantity stay for
   * the contractor to fill). */
  function addDraftFromRule(rule: PricingRuleResponse) {
    addDraft({
      category: rule.category,
      unit: rule.unit,
      unit_price_cents: Math.round(rule.baseline_cents * (rule.multiplier || 1)),
      description: "",
      quantity: 1,
      confidence: "high",
      visible_to_client: true,
      ai_notes: null,
    });
  }

  /** Add a line from an AI suggestion. Visibility defaults from the
   * suggestion's confidence (low → hidden by default). The
   * source_excerpt + ai_notes get folded into ai_notes so the
   * contractor sees the AI reasoning + verbatim quote on the row. */
  function addDraftFromSuggestion(suggestion: LineItemSuggestion) {
    const combinedNotes = suggestion.source_excerpt
      ? `Source: "${suggestion.source_excerpt}"\n${suggestion.ai_notes}`
      : suggestion.ai_notes;
    addDraft({
      category: suggestion.category,
      description: suggestion.description,
      quantity: suggestion.quantity,
      unit: suggestion.unit,
      unit_price_cents: suggestion.unit_price_cents,
      confidence: suggestion.confidence,
      visible_to_client: suggestion.confidence !== "low",
      ai_notes: combinedNotes,
    });
  }

  async function onFindMore() {
    suggest.reset();
    try {
      const result = await suggest.mutateAsync();
      setSuggestions(result.suggestions);
    } catch {
      // surfaced via suggest.error in the panel
      setSuggestions([]);
    }
  }

  function onSave() {
    update.mutate({
      markup_pct: markupPct,
      terms: terms.trim() || null,
      render_mode: renderMode,
      use_sectional_template: useSectionalTemplate,
      line_items: draft.map(({ _key: _, ...payload }) => payload),
    });
  }

  function onPromote(targetStatus: QuoteStatusValue) {
    update.mutate({ status: targetStatus }, { onSettled: () => setConfirmingStatus(null) });
  }

  const lowConfidenceCount = draft.filter((li) => li.confidence === "low").length;
  const hiddenCount = draft.filter((li) => !li.visible_to_client).length;

  const sendable = quote.status === "draft" || quote.status === "ready" || quote.status === "sent";
  const isResend = quote.has_share_link;

  return (
    <div className="space-y-6">
      {/* Datalists power the free-type-with-dropdown combobox UX on
          the category + unit fields below. Sourced from the org's
          pricing rules + the values already in the draft, so as soon
          as the contractor adds a new category once it's available
          for autocomplete on the next line. */}
      <datalist id="quote-categories">
        {categoryOptions.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <datalist id="quote-units">
        {unitOptions.map((u) => (
          <option key={u} value={u} />
        ))}
      </datalist>
      <datalist id="quote-rooms">
        {/* Suggest rooms the contractor's already used in this quote
            so adding the second/third "Master Bath" line doesn't
            require retyping the label. */}
        {Array.from(
          new Set(draft.map((li) => li.room).filter((r): r is string => Boolean(r))),
        )
          .sort()
          .map((room) => (
            <option key={room} value={room} />
          ))}
      </datalist>

      {/* Breadcrumb. Replaces the bare "Back to walkthrough" link
          with full nav context: Jobs › <job title> › Quote <id>.
          The contractor knows where they are AND what part of the
          job tree this quote belongs to. The walkthrough link
          (when applicable) lives in the editor toolbar later — it's
          a dev affordance, not page navigation. */}
      <nav
        className="flex flex-wrap items-center gap-1.5 text-xs text-graphite-soft"
        aria-label="Breadcrumb"
      >
        <Link to="/jobs" className="hover:text-graphite">
          Jobs
        </Link>
        <span aria-hidden className="text-graphite-faint">
          ›
        </span>
        <Link
          to={`/jobs/${quote.job_id}`}
          className="max-w-[20rem] truncate font-medium text-graphite-soft hover:text-graphite"
          title={jobQuery.data?.title ?? "Job"}
        >
          {jobQuery.data?.title ?? "Job"}
        </Link>
        <span aria-hidden className="text-graphite-faint">
          ›
        </span>
        <span className="font-mono text-graphite">
          Quote {shortId(quote.id).toUpperCase()}
        </span>
      </nav>

      {/* Status bar journey is reassuring during draft → ready →
          sent. Once the homeowner has signed (accepted) or declined,
          the AcceptanceAudit pill carries the same info more
          compactly and the journey bar becomes ceremonial. Hide it
          to give the project ledger room to breathe. */}
      {quote.status !== "accepted" && quote.status !== "declined" && (
        <QuoteStatusBar status={quote.status} />
      )}

      {(quote.status === "accepted" || quote.status === "declined") && (
        <AcceptanceAudit quote={quote} />
      )}

      {/* Project ledger (post-acceptance). Replaces the giant "Quote
          accepted" affirmation card with the actual run-the-project
          surface: financial KPIs, job lifecycle controls, change
          orders, payment ledger. The line-item editor below this
          collapses to a read-only summary by default — editing
          accepted quotes is rare + risky. */}
      {quote.status === "accepted" && jobQuery.data && (
        <ProjectLedger quote={quote} job={jobQuery.data} />
      )}

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-1.5">
            <h1 className="font-mono text-2xl font-bold tracking-tight">Quote</h1>
            <HelpPopover title="What you can do here">
              <p>
                Edit any line item — quantity, unit price, hide-from-client, add a note. Total
                recalculates as you change things.
              </p>
              <p>
                <strong>Markup</strong> applies on top of all line items; that's the "your profit"
                lever. <strong>Terms</strong> are the legal / payment language attached to the
                quote.
              </p>
              <p>
                Hit "Send to client" when ready. Homeowner gets an email with a link to view,
                accept, sign, and optionally pay a deposit. Their replies thread back into your
                Inbox.
              </p>
            </HelpPopover>
          </div>
          <p className="mt-1 font-mono text-xs text-graphite-soft tabular-nums">
            {shortId(quote.id)} · created {formatRelative(quote.created_at)}
            {quote.last_sent_at && (
              <> · last sent {formatRelative(quote.last_sent_at)}</>
            )}
            {quote.client_viewed_at && (
              <>
                {" "}
                · viewed {quote.view_count}× since{" "}
                {formatRelative(quote.client_viewed_at)}
              </>
            )}
            {quote.signed_name && (
              <>
                {" "}
                · signed by {quote.signed_name} {formatRelative(quote.accepted_at)}
              </>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {dirty && (
            <button
              type="button"
              onClick={onSave}
              disabled={update.isPending}
              className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" aria-hidden />
              {update.isPending ? "Saving…" : "Save changes"}
            </button>
          )}
          {!dirty && (
            <Link
              to={`/quotes/${quoteId}/preview`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite transition hover:bg-pencil-yellow-soft"
              title="See exactly what the homeowner will see — does not bump the view counter"
            >
              <Eye className="h-3.5 w-3.5" aria-hidden />
              Preview
            </Link>
          )}
          {!dirty && <PdfButtonGroup quoteId={quoteId} />}
          {!dirty && sendable && (
            <SendViaTextButton
              quoteId={quoteId}
              clientPhone={
                clientsQuery.data?.find(
                  (c) => c.id === jobQuery.data?.client_id,
                )?.phone ?? null
              }
            />
          )}
          {!dirty && sendable && (
            <button
              type="button"
              onClick={() => setSendOpen(true)}
              className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
              title="Send via email"
            >
              <Mail className="h-3.5 w-3.5" aria-hidden />
              {isResend ? "Resend to client" : "Send to client"}
            </button>
          )}
          {/* Manual reminder — only shown while in flight (sent,
              no decision yet). Mints a fresh share token + emails
              the same body as the auto-cron. Doesn't count
              against the org's reminder cap. */}
          {!dirty && quote.status === "sent" && (
            <button
              type="button"
              onClick={() => sendReminder.mutate()}
              disabled={sendReminder.isPending}
              className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-3 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-pencil-yellow-soft disabled:opacity-50"
              title="Send a reminder email to the client now. New share link; doesn't count against the auto-reminder cap."
            >
              <Bell className="h-3.5 w-3.5" aria-hidden />
              {sendReminder.isPending ? "Sending…" : "Send reminder"}
            </button>
          )}
          {/* Deposit-received button — accepted quote that hasn't
              been marked yet. Click opens the modal where the
              contractor confirms the amount + adds a payment-
              method note. Same /deposit-received endpoint as
              the iOS surface. */}
          {!dirty && quote.status === "accepted" && !quote.deposit_received_at && (
            <button
              type="button"
              onClick={() => setDepositOpen(true)}
              className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-3 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-pencil-yellow-soft"
              title="Record a deposit you received off-platform (Stripe, Square, check). Enables the homeowner-portal status tracker."
            >
              <DollarSign className="h-3.5 w-3.5" aria-hidden />
              Mark deposit received
            </button>
          )}
          {!dirty && (
            <StatusActions currentStatus={quote.status} onSelect={(s) => setConfirmingStatus(s)} />
          )}
        </div>
      </header>

      {update.error && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {update.error instanceof ApiError ? update.error.detail : "Could not save."}
        </p>
      )}

      {sendReminder.error && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {sendReminder.error instanceof ApiError
            ? sendReminder.error.detail
            : "Could not send reminder."}
        </p>
      )}
      {sendReminder.isSuccess && !sendReminder.isPending && (
        <p className="rounded border border-olive/30 bg-olive/5 px-3 py-2 text-sm text-olive">
          Reminder sent. The client received a fresh share link.
        </p>
      )}

      {/* xl 2-column layout: workspace (line items, markup, terms,
          accept-state ledger) on the left; client conversation
          rail on the right, sticky to the viewport top so it
          stays visible while editing line items. Below xl the
          rail collapses back into a vertical stack. */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_22rem]">
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Stat
              label={hasHiddenLines ? "Customer total" : "Total (incl. markup)"}
              value={formatCents(customerTotal)}
              subtitle={hasHiddenLines ? "What the client sees on their quote" : undefined}
              emphasis
            />
            {hasHiddenLines && (
              <Stat
                label="Internal total"
                value={formatCents(internalTotal)}
                subtitle="All lines including hidden — for your eyes only"
              />
            )}
            <Stat
              label={hasHiddenLines ? "Hidden gap" : "Subtotal"}
              value={
                hasHiddenLines
                  ? formatCents(internalTotal - customerTotal)
                  : formatCents(internalSubtotal)
              }
              subtitle={
                hasHiddenLines
                  ? `${draft.filter((li) => !li.visible_to_client).length} hidden line${
                      draft.filter((li) => !li.visible_to_client).length === 1 ? "" : "s"
                    }`
                  : undefined
              }
            />
            {!hasHiddenLines && <Stat label="Line items" value={`${draft.length}`} />}
          </div>

          {(lowConfidenceCount > 0 || hiddenCount > 0) && (
            <p className="rounded border border-burnt/30 bg-pencil-yellow-soft px-3 py-2 text-sm text-graphite">
              {lowConfidenceCount > 0 && (
                <>
                  <span className="font-semibold">{lowConfidenceCount}</span> low-confidence line
                  item{lowConfidenceCount === 1 ? "" : "s"} —{" "}
                </>
              )}
              {hiddenCount > 0 && (
                <>
                  <span className="font-semibold">{hiddenCount}</span> hidden from client. Review
                  before sending.
                </>
              )}
            </p>
          )}

          {/* Post-acceptance: gate the editor behind an explicit
              "Edit line items" toggle. Editing accepted quotes is a
              footgun — the change doesn't propagate to the signature.
              The collapsed state shows a one-line summary so the
              contractor still sees what's there. */}
          {quote.status === "accepted" && !editorExpanded ? (
            <section className="rounded-lg border border-rule bg-paper-warm p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-mono text-sm text-graphite-soft">
                  <span className="font-semibold text-graphite">{draft.length}</span>{" "}
                  line item{draft.length === 1 ? "" : "s"} · locked since
                  signature
                </p>
                <button
                  type="button"
                  onClick={() => setEditorExpanded(true)}
                  className="font-mono text-xs uppercase tracking-wider text-graphite-soft hover:text-graphite"
                >
                  Edit line items
                </button>
              </div>
            </section>
          ) : null}

          {/* Editor + markup + terms wrapper. Hidden when the quote is
              accepted AND the contractor hasn't explicitly opened the
              editor. Pre-acceptance always renders. */}
          {(quote.status !== "accepted" || editorExpanded) && (
          <>
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-mono text-lg font-bold tracking-tight">Line items</h2>
              <div className="flex flex-wrap items-center gap-2">
                {quote.status === "accepted" && (
                  <button
                    type="button"
                    onClick={() => setEditorExpanded(false)}
                    className="rounded border border-rule bg-paper px-2 py-1 font-mono text-xs font-semibold text-graphite-soft hover:text-graphite"
                    title="Hide the line-item editor"
                  >
                    Done editing
                  </button>
                )}
                {quote.walkthrough_id && (
                  <button
                    type="button"
                    onClick={onFindMore}
                    disabled={suggest.isPending}
                    title="Re-read the walkthrough for items the draft missed"
                    className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-2 py-1 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft disabled:opacity-50"
                  >
                    <Sparkles className="h-3 w-3" aria-hidden />
                    {suggest.isPending ? "Searching…" : "Find more from walkthrough"}
                  </button>
                )}
                <ApplyTemplateButton
                  quoteId={quoteId}
                  onApplied={() => {
                    void quoteQuery.refetch();
                  }}
                />
                <AddLineMenu
                  rules={rulesQuery.data ?? []}
                  onAddBlank={() => addDraft()}
                  onAddFromRule={addDraftFromRule}
                />
              </div>
            </div>

            {suggestions !== null && (
              <SuggestionsPanel
                suggestions={suggestions}
                error={
                  suggest.error
                    ? suggest.error instanceof ApiError
                      ? suggest.error.detail
                      : "Could not load suggestions."
                    : null
                }
                onAdd={(s) => {
                  addDraftFromSuggestion(s);
                  // Drop just the added one from the open panel.
                  setSuggestions((prev) => (prev ? prev.filter((x) => x !== s) : prev));
                }}
                onClose={() => {
                  setSuggestions(null);
                  suggest.reset();
                }}
              />
            )}

            {selectedKeys.size > 0 && (
              <BulkActionToolbar
                count={selectedKeys.size}
                onClear={clearSelection}
                onSetRoom={(room) => bulkPatch({ room: room || null })}
                onSetSection={(section) =>
                  bulkPatch({ section_code: section || null })
                }
                onSetKind={(kind) => bulkPatch({ kind })}
                onToggleVisibility={(visible) =>
                  bulkPatch({ visible_to_client: visible })
                }
                onDelete={bulkDelete}
              />
            )}

            {/* Card list — narrow viewports (iPad portrait + phone).
                Hides the desktop table and renders each line as a
                stacked card with bigger touch targets. Reorder
                here is via the up/down arrows; drag-and-drop only
                works on desktop. */}
            <ul className="space-y-3 lg:hidden">
              {draft.length === 0 && (
                <li className="rounded border border-rule bg-paper-warm py-8 text-center text-sm text-graphite-soft">
                  No line items. Hit "Add line" to enter one manually.
                </li>
              )}
              {draft.map((li, idx) => (
                <CardLineItem
                  key={li._key}
                  li={li}
                  idx={idx}
                  total={draft.length}
                  selected={selectedKeys.has(li._key)}
                  onToggleSelected={() => toggleRow(li._key)}
                  onPatch={(patch) => patchDraft(idx, patch)}
                  onMoveUp={() => moveRow(idx, -1)}
                  onMoveDown={() => moveRow(idx, 1)}
                  onRemove={() => removeDraft(idx)}
                  showRoom={showRoomColumn}
                  showSection={showSectionColumn}
                />
              ))}
            </ul>

            <div className="hidden overflow-x-auto rounded-lg border border-rule bg-paper-warm lg:block">
              <table className="w-full">
                <thead className="border-b border-rule bg-paper">
                  <tr>
                    <th className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected;
                        }}
                        onChange={toggleAll}
                        title={allSelected ? "Deselect all" : "Select all"}
                        aria-label="Select all rows"
                      />
                    </th>
                    {showRoomColumn && (
                      <th className="label-mono px-2 py-2 text-left">Room</th>
                    )}
                    {showSectionColumn && (
                      <th className="label-mono px-2 py-2 text-left">Sec.</th>
                    )}
                    <th className="label-mono px-2 py-2 text-left">Category</th>
                    <th className="label-mono px-2 py-2 text-left">Description</th>
                    <th className="label-mono px-2 py-2 text-right">Qty</th>
                    <th className="label-mono px-2 py-2 text-left">Unit</th>
                    <th className="label-mono px-2 py-2 text-right">Unit $</th>
                    <th className="label-mono px-2 py-2 text-right">Total</th>
                    <th className="label-mono px-2 py-2 text-center">Kind</th>
                    {showConfColumn && (
                      <th className="label-mono px-2 py-2 text-center">Conf.</th>
                    )}
                    <th className="label-mono px-2 py-2 text-center">Visible</th>
                    <th className="label-mono px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {draft.length === 0 && (
                    <tr>
                      <td colSpan={visibleColCount} className="px-4 py-8 text-center text-sm text-graphite-soft">
                        No line items. Hit "Add line" to enter one manually, or generate a fresh
                        quote from the walkthrough.
                      </td>
                    </tr>
                  )}
                  {draft.map((li, idx) => (
                    <tr
                      key={li._key}
                      draggable
                      onDragStart={() => onRowDragStart(idx)}
                      onDragEnd={onRowDragEnd}
                      onDragOver={onRowDragOver}
                      onDrop={() => onRowDrop(idx)}
                      className={
                        selectedKeys.has(li._key)
                          ? "cursor-grab border-b border-rule bg-pencil-yellow-soft last:border-0 active:cursor-grabbing"
                          : "cursor-grab border-b border-rule last:border-0 active:cursor-grabbing"
                      }
                    >
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={selectedKeys.has(li._key)}
                          onChange={() => toggleRow(li._key)}
                          aria-label={`Select line ${idx + 1}`}
                        />
                      </td>
                      {showRoomColumn && (
                        <td className="px-2 py-2">
                          <input
                            type="text"
                            list="quote-rooms"
                            value={li.room ?? ""}
                            onChange={(e) =>
                              patchDraft(idx, {
                                room: e.target.value.trim() ? e.target.value : null,
                              })
                            }
                            placeholder="—"
                            className="w-28 rounded border border-rule bg-paper px-1.5 py-1 font-mono text-xs text-graphite focus:border-graphite focus:outline-none"
                          />
                        </td>
                      )}
                      {showSectionColumn && (
                        <td className="px-2 py-2">
                          <select
                            value={li.section_code ?? ""}
                            onChange={(e) =>
                              patchDraft(idx, {
                                section_code:
                                  (e.target.value as DraftLine["section_code"]) || null,
                              })
                            }
                            className="rounded border border-rule bg-paper px-1 py-0.5 font-mono text-[10px] uppercase text-graphite-soft"
                            title="A–R: canonical 18-section template position. Leave blank if not using sectional rendering."
                          >
                            <option value="">—</option>
                            <option value="a">A</option>
                            <option value="b">B</option>
                            <option value="c">C</option>
                            <option value="d">D</option>
                            <option value="e">E</option>
                            <option value="f">F</option>
                            <option value="g">G</option>
                            <option value="h">H</option>
                            <option value="i">I</option>
                            <option value="j">J</option>
                            <option value="k">K</option>
                            <option value="l">L</option>
                            <option value="m">M</option>
                            <option value="n">N</option>
                            <option value="o">O</option>
                            <option value="p">P</option>
                            <option value="q">Q</option>
                            <option value="r">R</option>
                          </select>
                        </td>
                      )}
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          list="quote-categories"
                          value={li.category}
                          onChange={(e) => patchDraft(idx, { category: e.target.value })}
                          className="w-full rounded border border-rule bg-paper px-1.5 py-1 font-mono text-xs text-graphite focus:border-graphite focus:outline-none"
                        />
                      </td>
                      <td className="min-w-[18rem] px-2 py-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={li.description}
                            onChange={(e) =>
                              patchDraft(idx, { description: e.target.value })
                            }
                            className="w-full rounded border border-rule bg-paper px-1.5 py-1 text-sm text-graphite focus:border-graphite focus:outline-none"
                          />
                          {li.ai_notes && (
                            <AiRationalePopover
                              note={li.ai_notes}
                              sourceExcerpt={li.source_excerpt ?? null}
                              sourceTimestampSeconds={
                                li.source_timestamp_seconds ?? null
                              }
                              walkthroughId={quote.walkthrough_id}
                            />
                          )}
                        </div>
                        {li.assumptions && (
                          <p className="mt-1 text-[11px] italic text-graphite-soft">
                            {li.assumptions}
                          </p>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={li.quantity}
                          onChange={(e) =>
                            patchDraft(idx, { quantity: Number(e.target.value) || 0 })
                          }
                          className="w-20 rounded border border-rule bg-paper px-1.5 py-1 text-right font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          list="quote-units"
                          value={li.unit}
                          onChange={(e) => patchDraft(idx, { unit: e.target.value })}
                          className="w-16 rounded border border-rule bg-paper px-1.5 py-1 font-mono text-xs text-graphite focus:border-graphite focus:outline-none"
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={(li.unit_price_cents / 100).toFixed(2)}
                          onChange={(e) =>
                            patchDraft(idx, {
                              unit_price_cents: Math.round((Number(e.target.value) || 0) * 100),
                            })
                          }
                          className="w-24 rounded border border-rule bg-paper px-1.5 py-1 text-right font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
                        />
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-sm tabular-nums">
                        {(li.kind ?? "firm") === "tbd" ? (
                          <span className="text-graphite-faint">—</span>
                        ) : (
                          formatCents(li.quantity * li.unit_price_cents)
                        )}
                      </td>
                      <td className="px-2 py-2 text-center">
                        {/* Line-item kind. firm = legacy default, allowance =
                            soft-commit budget line, client_supplied = labor
                            only, tbd = placeholder with no price (skipped from
                            total). Affects badging on the customer view + PDF. */}
                        <select
                          value={li.kind ?? "firm"}
                          onChange={(e) =>
                            patchDraft(idx, {
                              kind: e.target.value as LineItemKind,
                            })
                          }
                          className={[
                            "rounded border px-1 py-0.5 font-mono text-[10px]",
                            (li.kind ?? "firm") === "firm"
                              ? "border-rule bg-paper text-graphite-soft"
                              : (li.kind ?? "firm") === "allowance"
                                ? "border-pencil-yellow/40 bg-pencil-yellow-soft text-graphite"
                                : (li.kind ?? "firm") === "client_supplied"
                                  ? "border-rule bg-paper-warm text-graphite"
                                  : "border-burnt/30 bg-burnt/10 text-burnt",
                          ].join(" ")}
                          title={
                            (li.kind ?? "firm") === "firm"
                              ? "Fixed-price line"
                              : (li.kind ?? "firm") === "allowance"
                                ? "Budget reservation; counts toward total at allowance amount"
                                : (li.kind ?? "firm") === "client_supplied"
                                  ? "Homeowner provides materials; charge labor only"
                                  : "Placeholder; renders as 'Pending', does NOT count toward total"
                          }
                        >
                          <option value="firm">firm</option>
                          <option value="allowance">allow</option>
                          <option value="client_supplied">client</option>
                          <option value="tbd">tbd</option>
                        </select>
                      </td>
                      {showConfColumn && (
                        <td className="px-2 py-2 text-center">
                          <select
                            value={li.confidence}
                            onChange={(e) =>
                              patchDraft(idx, {
                                confidence: e.target.value as LineItemConfidence,
                              })
                            }
                            className={[
                              "rounded border px-1 py-0.5 font-mono text-[10px]",
                              li.confidence === "high"
                                ? "border-pencil-yellow/40 bg-pencil-yellow-soft text-graphite"
                                : li.confidence === "medium"
                                  ? "border-rule bg-paper text-graphite-soft"
                                  : "border-red-pencil/30 bg-red-pencil/10 text-red-pencil",
                            ].join(" ")}
                          >
                            <option value="high">high</option>
                            <option value="medium">med</option>
                            <option value="low">low</option>
                          </select>
                        </td>
                      )}
                      <td className="px-2 py-2 text-center">
                        <button
                          type="button"
                          onClick={() =>
                            patchDraft(idx, { visible_to_client: !li.visible_to_client })
                          }
                          title={li.visible_to_client ? "Visible to client" : "Hidden from client"}
                          className="rounded p-1 text-graphite-soft transition hover:bg-paper hover:text-graphite"
                        >
                          {li.visible_to_client ? (
                            <Eye className="h-3.5 w-3.5" aria-hidden />
                          ) : (
                            <EyeOff className="h-3.5 w-3.5 text-burnt" aria-hidden />
                          )}
                        </button>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <div className="inline-flex items-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => moveRow(idx, -1)}
                            disabled={idx === 0}
                            title="Move up"
                            aria-label="Move row up"
                            className="rounded p-1 text-graphite-soft transition hover:bg-paper hover:text-graphite disabled:opacity-25"
                          >
                            <ChevronUp className="h-3 w-3" aria-hidden />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveRow(idx, 1)}
                            disabled={idx === draft.length - 1}
                            title="Move down"
                            aria-label="Move row down"
                            className="rounded p-1 text-graphite-soft transition hover:bg-paper hover:text-graphite disabled:opacity-25"
                          >
                            <ChevronDown className="h-3 w-3" aria-hidden />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeDraft(idx)}
                            title="Remove line"
                            className="ml-1 rounded p-1 text-graphite-soft transition hover:bg-red-pencil/10 hover:text-red-pencil"
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block">
              <span className="label-mono">Markup (%)</span>
              <input
                type="number"
                step="1"
                min="0"
                max="200"
                value={Math.round(markupPct * 100)}
                onChange={(e) => setMarkupPct(Number(e.target.value) / 100)}
                className="mt-1 w-full rounded border border-rule bg-paper-warm px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none"
              />
            </label>

            <div className="flex items-end justify-end">
              <div className="text-right font-mono text-sm">
                <p className="text-graphite-soft">
                  Customer:{" "}
                  <span className="font-bold text-graphite">{formatCents(customerTotal)}</span>
                </p>
                {hasHiddenLines && (
                  <p className="mt-0.5 text-graphite-soft">
                    Internal: <span className="text-graphite">{formatCents(internalTotal)}</span>
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Sectional template toggle. When on, the homeowner-facing
              render groups line items under the canonical 18-section
              A–R skeleton (A. Site preparation → R. Construction
              waste removal). Empty sections render "No action
              required in this section" so the customer sees what's
              NOT included by being explicit about scope coverage. */}
          <section className="space-y-2">
            <div className="flex items-baseline gap-2">
              <span className="label-mono">18-section template</span>
              <HelpPopover title="What this is">
                <p>
                  Groups the homeowner's view of the scope under the canonical 18-section
                  A–R structure (Site preparation through Construction waste removal). Empty
                  sections show "No action required" — the way experienced remodelers signal
                  scope coverage so clients see what's <em>not</em> included.
                </p>
                <p>
                  Set the section letter on each line item below to assign it. Lines without
                  an assigned section appear under "Additional scope" above the grid.
                </p>
              </HelpPopover>
            </div>
            <label className="inline-flex items-center gap-2 font-mono text-sm text-graphite">
              <input
                type="checkbox"
                checked={useSectionalTemplate}
                onChange={(e) => setUseSectionalTemplate(e.target.checked)}
                className="h-4 w-4 rounded border-rule"
              />
              Use 18-section template on the customer view
            </label>
          </section>

          {/* Customer-view toggle. The line items (above) and totals
              (above) stay the contractor's working view regardless;
              this only changes what the homeowner sees on the public
              quote link + the email PDF attachment. Itemized = full
              line-item table. Artisan = scope narrative + one
              total, the way experienced remodelers ship a bid. */}
          <section className="space-y-2">
            <div className="flex items-baseline gap-2">
              <span className="label-mono">Customer view</span>
              <HelpPopover title="What this changes">
                <p>
                  <strong>Itemized</strong> shows the homeowner every line item, qty, and unit
                  price. Good for transparent fixed-price work.
                </p>
                <p>
                  <strong>Artisan</strong> hides the line-item table from the homeowner and shows
                  just the project description + grand total. The way experienced remodelers ship
                  a bid — one price for the room, scope as prose, no nickel-and-diming. Your
                  internal cost build-up doesn't change either way.
                </p>
              </HelpPopover>
            </div>
            <div className="inline-flex rounded-lg border border-rule bg-paper-warm p-1">
              <button
                type="button"
                onClick={() => setRenderMode("itemized")}
                className={
                  "rounded px-3 py-1.5 font-mono text-xs font-semibold transition " +
                  (renderMode === "itemized"
                    ? "bg-graphite text-paper"
                    : "text-graphite-soft hover:text-graphite")
                }
                aria-pressed={renderMode === "itemized"}
              >
                Itemized
              </button>
              <button
                type="button"
                onClick={() => setRenderMode("artisan")}
                className={
                  "rounded px-3 py-1.5 font-mono text-xs font-semibold transition " +
                  (renderMode === "artisan"
                    ? "bg-graphite text-paper"
                    : "text-graphite-soft hover:text-graphite")
                }
                aria-pressed={renderMode === "artisan"}
              >
                Artisan
              </button>
            </div>
            {renderMode === "artisan" && (
              <p className="font-mono text-xs text-graphite-soft">
                Homeowner sees the project description + total. The line items above stay
                internal — you'll still use them for your cost build-up and any internal PDF.
              </p>
            )}
          </section>

          <section className="space-y-3">
            <h2 className="font-mono text-lg font-bold tracking-tight">Terms</h2>
            <textarea
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              rows={4}
              placeholder="50% deposit, 25% midpoint, 25% completion. 12-month workmanship warranty…"
              className="w-full rounded-lg border border-rule bg-paper-warm p-3 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
            />
          </section>
          </>
          )}
        </div>

        {/* Right rail (xl+): persistent client card + conversation.
            Sticks to top so it doesn't scroll out of sight while the
            contractor scrolls through 30 line items below. */}
        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start xl:max-h-[calc(100vh-3rem)] xl:overflow-y-auto">
          {/* Client card — keeps the homeowner's name + contact at
              hand without a navigation jump back to the client
              record. ``quote.client_*`` fields are denormalized
              from the linked Client on every quote response. */}
          {(quote.client_name || quote.client_email) && (
            <section className="rounded-lg border border-rule bg-paper-warm p-3">
              <div className="flex items-baseline justify-between">
                <span className="label-mono">Client</span>
                {quote.client_id && (
                  <Link
                    to={`/clients/${quote.client_id}`}
                    className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft hover:text-graphite"
                  >
                    Open record
                  </Link>
                )}
              </div>
              {quote.client_name && (
                <p className="mt-1 text-sm font-semibold text-graphite">
                  {quote.client_name}
                </p>
              )}
              {quote.client_email && (
                <a
                  href={`mailto:${quote.client_email}`}
                  className="mt-0.5 block font-mono text-xs text-graphite-soft hover:text-graphite"
                >
                  {quote.client_email}
                </a>
              )}
            </section>
          )}

          <CommentThread
            comments={commentsQuery.data ?? []}
            loading={commentsQuery.isLoading}
            showReplyButton
            composeLabel="Reply to client"
            composePlaceholder="Type your reply — the client gets an email and sees it on their quote page."
            submitting={postComment.isPending}
            apiBase={apiBase}
            allowAttachments
            errorMessage={
              postComment.error
                ? postComment.error instanceof ApiError
                  ? postComment.error.detail
                  : "Could not post."
                : null
            }
            onSubmit={async ({ body: text, parentId, files }) => {
              await postComment.mutateAsync({
                body: text,
                parent_comment_id: parentId,
                attachments: files,
              });
            }}
            renderAttachmentAction={({ comment, attachment }) => (
              <button
                type="button"
                onClick={() => setPromoteTarget({ comment, attachment })}
                className="inline-flex items-center gap-1 rounded border border-rule bg-paper px-2 py-1 font-mono text-[10px] font-semibold text-graphite-soft transition hover:border-graphite hover:bg-pencil-yellow-soft hover:text-graphite"
              >
                <Send className="h-3 w-3" aria-hidden />
                Send to job
              </button>
            )}
          />
        </aside>
      </div>

      {depositOpen && (
        <DepositReceivedDialog
          totalCents={quote.total_cents}
          submitting={markDeposit.isPending}
          error={
            markDeposit.error
              ? markDeposit.error instanceof ApiError
                ? markDeposit.error.detail
                : "Could not save."
              : null
          }
          onCancel={() => {
            setDepositOpen(false);
            markDeposit.reset();
          }}
          onSubmit={(payload) => {
            markDeposit.mutate(payload, {
              onSuccess: () => {
                setDepositOpen(false);
                markDeposit.reset();
              },
            });
          }}
        />
      )}

      {sendOpen && (
        <SendQuoteDialog
          clients={clientsQuery.data ?? []}
          linkedClientId={jobQuery.data?.client_id ?? null}
          isResend={isResend}
          submitting={send.isPending}
          error={send.error ?? null}
          onCancel={() => {
            setSendOpen(false);
            send.reset();
          }}
          onSubmit={(payload) => {
            send.mutate(payload, {
              onSuccess: ({ share_url }) => {
                setLastSendShareUrl(share_url);
                setSendOpen(false);
              },
            });
          }}
        />
      )}

      {lastSendShareUrl && (
        <SendSuccessToast shareUrl={lastSendShareUrl} onDismiss={() => setLastSendShareUrl(null)} />
      )}

      {promoteTarget && (
        <PromoteAttachmentDialog
          attachment={promoteTarget.attachment}
          submitting={promoteAttachment.isPending}
          error={
            promoteAttachment.error
              ? promoteAttachment.error instanceof ApiError
                ? promoteAttachment.error.detail
                : "Could not send to job."
              : null
          }
          onCancel={() => {
            setPromoteTarget(null);
            promoteAttachment.reset();
          }}
          onSubmit={(label) => {
            if (!promoteTarget) return;
            promoteAttachment.mutate(
              {
                commentId: promoteTarget.comment.id,
                attachmentId: promoteTarget.attachment.id,
                label,
              },
              {
                onSuccess: (created) => {
                  setPromoteSuccessLabel(created.label);
                  setPromoteTarget(null);
                  promoteAttachment.reset();
                },
              },
            );
          }}
        />
      )}

      {promoteSuccessLabel && (
        <SendToJobToast
          label={promoteSuccessLabel}
          onDismiss={() => setPromoteSuccessLabel(null)}
        />
      )}

      {confirmingStatus && (
        <ConfirmDialog
          title={`Move quote to "${
            STATUS_FLOW.find((s) => s.value === confirmingStatus)?.label ?? confirmingStatus
          }"?`}
          body={
            <>
              {confirmingStatus === "sent" &&
                "This locks the quote for the client. Save any pending edits first."}
              {confirmingStatus === "accepted" && "Records that the client accepted this quote."}
              {confirmingStatus === "declined" && "Records that the client declined."}
              {(confirmingStatus === "ready" || confirmingStatus === "draft") &&
                "Updates the quote's lifecycle stage."}
            </>
          }
          confirmLabel="Update status"
          busy={update.isPending}
          onConfirm={() => onPromote(confirmingStatus)}
          onCancel={() => setConfirmingStatus(null)}
        />
      )}
    </div>
  );
}

function StatusActions({
  currentStatus,
  onSelect,
}: {
  currentStatus: QuoteStatusValue;
  onSelect: (s: QuoteStatusValue) => void;
}) {
  const next: QuoteStatusValue[] =
    currentStatus === "draft"
      ? ["ready"]
      : currentStatus === "ready"
        ? ["sent", "draft"]
        : currentStatus === "sent"
          ? ["accepted", "declined"]
          : [];
  if (next.length === 0) return null;
  return (
    <div className="flex gap-2">
      {next.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onSelect(s)}
          className="rounded border border-rule bg-paper-warm px-3 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft"
        >
          → {STATUS_FLOW.find((x) => x.value === s)?.label ?? s}
        </button>
      ))}
    </div>
  );
}

function Stat({
  label,
  value,
  subtitle,
  emphasis = false,
}: {
  label: string;
  value: string;
  subtitle?: string;
  emphasis?: boolean;
}) {
  return (
    <div className="rounded-lg border border-rule bg-paper-warm px-4 py-3">
      <span className="label-mono">{label}</span>
      <p
        className={`mt-1 font-mono tabular-nums ${
          emphasis ? "text-2xl font-bold text-graphite" : "text-base text-graphite"
        }`}
      >
        {value}
      </p>
      {subtitle && <p className="mt-0.5 font-mono text-[10px] text-graphite-soft">{subtitle}</p>}
    </div>
  );
}

// ---------------------------------------------------------------
// AddLineMenu — primary "Add line" button + dropdown of pricing rules
// ---------------------------------------------------------------

function AddLineMenu({
  rules,
  onAddBlank,
  onAddFromRule,
}: {
  rules: PricingRuleResponse[];
  onAddBlank: () => void;
  onAddFromRule: (rule: PricingRuleResponse) => void;
}) {
  const [open, setOpen] = useState(false);

  // Click-outside dismiss.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      if (!target?.closest("[data-add-line-menu]")) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative" data-add-line-menu>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded bg-graphite px-2 py-1 font-mono text-xs font-semibold text-paper transition hover:bg-graphite-soft"
      >
        <Plus className="h-3 w-3" aria-hidden /> Add line
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-72 overflow-hidden rounded-lg border border-rule bg-paper-warm shadow-lg">
          <button
            type="button"
            onClick={() => {
              onAddBlank();
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 border-b border-rule px-3 py-2 text-left text-sm text-graphite transition hover:bg-pencil-yellow-soft"
          >
            <Plus className="h-3 w-3" aria-hidden />
            Blank line (no rule)
          </button>
          {rules.length === 0 ? (
            <p className="px-3 py-3 text-xs text-graphite-soft">
              No pricing rules — add some on the Pricing rules page to get one-click line items
              here.
            </p>
          ) : (
            <ul className="max-h-72 overflow-y-auto py-1">
              {rules.map((rule) => {
                const effectiveCents = Math.round(rule.baseline_cents * (rule.multiplier || 1));
                return (
                  <li key={rule.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onAddFromRule(rule);
                        setOpen(false);
                      }}
                      className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm transition hover:bg-pencil-yellow-soft"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-mono text-xs text-graphite">
                          {rule.category}
                        </div>
                        <div className="font-mono text-[10px] text-graphite-soft">{rule.tier}</div>
                      </div>
                      <div className="font-mono text-xs tabular-nums text-graphite-soft">
                        {formatCents(effectiveCents)} / {rule.unit}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// ApplyTemplateButton — layer a JobTemplate's line items onto this
// quote. Provenance protects AI + manual rows; replace_existing
// only swaps prior template rows.
// ---------------------------------------------------------------

function ApplyTemplateButton({
  quoteId,
  onApplied,
}: {
  quoteId: string;
  onApplied: () => void;
}) {
  const templates = useJobTemplates();
  const apply = useApplyTemplateToQuote(quoteId);
  const [open, setOpen] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [resultMsg, setResultMsg] = useState<string | null>(null);

  // Click-outside dismiss + ESC close.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      if (!target?.closest("[data-apply-tpl]")) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Hide the button entirely when the org has no templates yet —
  // the empty popover would just frustrate.
  if (templates.data && templates.data.length === 0) {
    return null;
  }

  function handleApply(templateId: string) {
    setErrorMsg(null);
    setResultMsg(null);
    apply.mutate(
      { template_id: templateId, replace_existing: replaceExisting },
      {
        onSuccess: (resp) => {
          const parts: string[] = [];
          if (resp.items_added > 0) {
            parts.push(`Added ${resp.items_added}`);
          }
          if (resp.items_replaced > 0) {
            parts.push(`replaced ${resp.items_replaced}`);
          }
          if (resp.items_preserved > 0) {
            parts.push(`kept ${resp.items_preserved}`);
          }
          setResultMsg(parts.join(" · ") || "Template applied.");
          onApplied();
          setOpen(false);
        },
        onError: (err) => {
          setErrorMsg(err.message);
        },
      },
    );
  }

  return (
    <div className="relative" data-apply-tpl>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setErrorMsg(null);
        }}
        disabled={apply.isPending}
        className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper-warm px-2 py-1 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft disabled:opacity-50"
        title="Layer a saved template's line items onto this quote"
      >
        <Layers className="h-3 w-3" aria-hidden />
        {apply.isPending ? "Applying…" : "Apply template"}
      </button>
      {resultMsg && !open && (
        <span className="absolute right-0 top-full mt-1 whitespace-nowrap rounded bg-graphite px-2 py-0.5 font-mono text-[10px] text-paper">
          {resultMsg}
        </span>
      )}
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-80 overflow-hidden rounded-lg border border-rule bg-paper-warm shadow-lg">
          <div className="border-b border-rule px-3 py-2">
            <p className="font-mono text-[11px] uppercase tracking-wider text-graphite-soft">
              Apply template
            </p>
            <p className="mt-1 text-xs text-graphite-soft">
              AI + manual rows are always preserved.
            </p>
            <label className="mt-2 flex items-center gap-2 text-xs text-graphite">
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
              />
              Replace prior template rows on this quote
            </label>
          </div>
          {templates.isLoading ? (
            <p className="px-3 py-3 text-xs text-graphite-soft">Loading…</p>
          ) : (
            <ul className="max-h-64 overflow-y-auto">
              {(templates.data ?? []).map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => handleApply(t.id)}
                    disabled={apply.isPending}
                    className="flex w-full items-start justify-between gap-2 border-b border-rule/60 px-3 py-2 text-left transition hover:bg-pencil-yellow-soft disabled:opacity-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-graphite">{t.name}</div>
                      {t.default_description && (
                        <div className="mt-0.5 truncate font-mono text-[11px] text-graphite-soft">
                          {t.default_description}
                        </div>
                      )}
                    </div>
                    <span className="font-mono text-[10px] text-graphite-faint">
                      {t.default_line_items.length} line
                      {t.default_line_items.length === 1 ? "" : "s"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {errorMsg && (
            <p className="border-t border-rule px-3 py-2 text-xs text-red-pencil">{errorMsg}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// SuggestionsPanel — AI proposals from "Find more from walkthrough"
// ---------------------------------------------------------------

function SuggestionsPanel({
  suggestions,
  error,
  onAdd,
  onClose,
}: {
  suggestions: LineItemSuggestion[];
  error: string | null;
  onAdd: (s: LineItemSuggestion) => void;
  onClose: () => void;
}) {
  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 font-mono text-sm font-semibold uppercase tracking-wider">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Suggested additions
          </h3>
          <p className="mt-1 text-xs text-graphite-soft">
            Things the contractor mentioned in the walkthrough that the current draft doesn't cover
            yet. Each one quotes the source line — verify before adding.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-graphite-soft hover:text-graphite"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {error && (
        <p className="mt-3 rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {error}
        </p>
      )}

      {!error && suggestions.length === 0 && (
        <p className="mt-3 rounded border border-rule bg-paper px-3 py-3 text-sm text-graphite-soft">
          Nothing missing — the AI thinks the draft already covers what the contractor mentioned.
          Manual add still works for things off-script.
        </p>
      )}

      {!error && suggestions.length > 0 && (
        <ul className="mt-3 space-y-2">
          {suggestions.map((s, idx) => (
            <li key={`${s.category}-${idx}`} className="rounded-lg border border-rule bg-paper p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-graphite">{s.description}</span>
                    <span
                      className={[
                        "rounded px-1.5 py-0.5 font-mono text-[10px]",
                        s.confidence === "high"
                          ? "bg-pencil-yellow-soft text-graphite"
                          : s.confidence === "medium"
                            ? "bg-paper-warm text-graphite-soft"
                            : "bg-red-pencil/10 text-red-pencil",
                      ].join(" ")}
                    >
                      {s.confidence}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 font-mono text-xs text-graphite-soft tabular-nums">
                    <span>{s.category}</span>
                    <span>
                      {s.quantity} {s.unit} × {formatCents(s.unit_price_cents)} ={" "}
                      {formatCents(Math.round(s.quantity * s.unit_price_cents))}
                    </span>
                  </div>
                  {s.source_excerpt && (
                    <p className="mt-2 border-l-2 border-pencil-yellow-soft pl-2 text-xs italic text-graphite">
                      "{s.source_excerpt}"
                    </p>
                  )}
                  {s.ai_notes && (
                    <p className="mt-1 font-mono text-[10px] text-graphite-soft">
                      AI: {s.ai_notes}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onAdd(s)}
                  className="inline-flex flex-none items-center gap-1.5 rounded bg-graphite px-2 py-1 font-mono text-xs font-semibold text-paper transition hover:bg-graphite-soft"
                >
                  <Plus className="h-3 w-3" aria-hidden />
                  Add
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// SendQuoteDialog — modal form to email the quote to the client
// ---------------------------------------------------------------

/** Special select-value sentinel meaning "send to the email I'm
 * about to type below" — separates manual entry from picking a
 * registered client. */
const CUSTOM_RECIPIENT = "__custom__";

function SendQuoteDialog({
  clients,
  linkedClientId,
  isResend,
  submitting,
  error,
  onCancel,
  onSubmit,
}: {
  clients: ClientResponse[];
  linkedClientId: string | null;
  isResend: boolean;
  submitting: boolean;
  /** Raw mutation error so we can branch on the structured error
   *  code (e.g. ``missing_business_contact``) and render a "Go to
   *  Settings" CTA instead of a generic toast. ``null`` when no
   *  error / not in flight. */
  error: ApiError | Error | null;
  onCancel: () => void;
  onSubmit: (payload: { recipient_override?: string; custom_message?: string }) => void;
}) {
  // Pre-select: the job's linked client if it has an email, else the
  // first client in the org with an email, else the custom-entry path.
  const initialClientId = useMemo(() => {
    if (linkedClientId) {
      const linked = clients.find((c) => c.id === linkedClientId);
      if (linked?.email) return linked.id;
    }
    const firstWithEmail = clients.find((c) => c.email);
    return firstWithEmail?.id ?? CUSTOM_RECIPIENT;
  }, [clients, linkedClientId]);

  const [selectedClientId, setSelectedClientId] = useState(initialClientId);
  const [customEmail, setCustomEmail] = useState("");
  const [message, setMessage] = useState("");

  // Resolve the recipient email being shown / sent.
  const selectedClient =
    selectedClientId !== CUSTOM_RECIPIENT ? clients.find((c) => c.id === selectedClientId) : null;
  const recipient =
    selectedClient?.email ?? (selectedClientId === CUSTOM_RECIPIENT ? customEmail : "");
  const canSubmit = recipient.trim().length > 0;

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onSubmit({
      recipient_override: recipient.trim() || undefined,
      custom_message: message.trim() || undefined,
    });
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: custom modal pattern; native <dialog> has different semantics (showModal/close lifecycle, top-layer rendering) that this component doesn't use.
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-graphite/40 px-6 py-12"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
      onKeyDown={(e) => {
        // Esc dismisses (mirrors backdrop click).
        if (e.key === "Escape" && !submitting) onCancel();
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-lg border border-rule bg-paper-warm shadow-lg"
      >
        <div className="flex items-start gap-3 px-5 py-4">
          <span className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full bg-pencil-yellow-soft">
            <Mail className="h-4 w-4 text-graphite" aria-hidden />
          </span>
          <div className="flex-1">
            <h2 className="font-mono text-base font-bold tracking-tight text-graphite">
              {isResend ? "Resend quote to client" : "Send quote to client"}
            </h2>
            <p className="mt-1 text-sm text-graphite-soft">
              We email a one-tap link to your client. They view the quote (line items hidden from
              them stay hidden), then accept with a typed signature or decline.{" "}
              {isResend && (
                <span className="text-burnt">Resending invalidates any in-flight link.</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            aria-label="Cancel"
            className="text-graphite-soft hover:text-graphite disabled:opacity-50"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="space-y-3 px-5 pb-4">
          <label className="block">
            <span className="label-mono">Send to</span>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none"
            >
              {clients
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => {
                  const hasEmail = Boolean(c.email);
                  const linked = c.id === linkedClientId;
                  return (
                    <option key={c.id} value={c.id} disabled={!hasEmail}>
                      {c.name}
                      {c.email ? ` — ${c.email}` : " — (no email)"}
                      {linked ? "  (this job's client)" : ""}
                    </option>
                  );
                })}
              <option value={CUSTOM_RECIPIENT}>Custom email…</option>
            </select>
            {selectedClient && (
              <span className="mt-1 block font-mono text-[10px] text-graphite-soft">
                Resolved to {selectedClient.email}
              </span>
            )}
          </label>

          {selectedClientId === CUSTOM_RECIPIENT && (
            <label className="block">
              <span className="label-mono">Custom email</span>
              <input
                type="email"
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                placeholder="someone@example.com"
                className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
              />
            </label>
          )}

          <p className="font-mono text-[10px] text-graphite-soft">
            Replies route to your address (set as Reply-To); the recipient sees your contractor
            company as the sender.
          </p>

          <label className="block">
            <span className="label-mono">Personal note (optional)</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Hi Jane — here's the kitchen remodel quote we discussed Tuesday. Let me know if anything looks off."
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
            />
          </label>

          {error &&
            (error instanceof ApiError && error.errorCode === "missing_business_contact" ? (
              <div className="rounded border border-pencil-yellow bg-pencil-yellow-soft px-3 py-3 text-sm">
                <p className="font-semibold text-graphite">
                  Set up your business contact info first
                </p>
                <p className="mt-1 text-graphite-soft">
                  {error instanceof ApiError ? error.detail : "—"}
                </p>
                <Link
                  to="/settings"
                  onClick={onCancel}
                  className="mt-2 inline-flex items-center gap-1 rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft"
                >
                  Open Settings →
                </Link>
              </div>
            ) : (
              <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
                {error instanceof ApiError ? error.detail : "Could not send."}
              </p>
            ))}
        </div>

        <div className="flex justify-end gap-2 border-t border-rule bg-paper px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft transition hover:text-graphite disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !canSubmit}
            className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" aria-hidden />
            {submitting ? "Sending…" : isResend ? "Resend" : "Send"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------
// SendSuccessToast — share URL surfaced after a successful send
// ---------------------------------------------------------------

function SendSuccessToast({
  shareUrl,
  onDismiss,
}: {
  shareUrl: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked; fall through to manual select */
    }
  }

  return (
    <div className="rounded-lg border border-pencil-yellow/60 bg-pencil-yellow-soft p-4">
      <div className="flex items-start gap-3">
        <Mail className="mt-0.5 h-4 w-4 text-graphite" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-graphite">
            <span className="font-medium">Sent.</span> The client also gets this link by email —
            copy it if you want to send through another channel.
          </p>
          <code className="mt-2 block overflow-x-auto rounded border border-rule bg-paper px-2 py-1 font-mono text-xs text-graphite">
            {shareUrl}
          </code>
        </div>
        <div className="flex flex-none flex-col items-end gap-1">
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded border border-rule bg-paper px-2 py-1 font-mono text-xs font-semibold text-graphite transition hover:bg-paper-warm"
          >
            <Copy className="h-3 w-3" aria-hidden />
            {copied ? "Copied!" : "Copy"}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="font-mono text-[10px] text-graphite-soft hover:text-graphite"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// AcceptanceAudit — contractor-side audit card for signed / declined
// ---------------------------------------------------------------

/** Parse a raw User-Agent string into a readable "Chrome on macOS"
 * label. Best-effort heuristic — the full UA stays in the
 * collapsible audit details so legal compliance is preserved. */
function parseUserAgent(ua: string | null | undefined): string {
  if (!ua) return "—";
  let browser = "browser";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/OPR\//.test(ua)) browser = "Opera";
  else if (/Chrome\//.test(ua)) browser = "Chrome";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Safari\//.test(ua)) browser = "Safari";

  let os = "device";
  if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/Mac OS X|Macintosh/.test(ua)) os = "macOS";
  else if (/Windows/.test(ua)) os = "Windows";
  else if (/Linux/.test(ua)) os = "Linux";

  return `${browser} on ${os}`;
}

/** Compact one-line acceptance card. The full audit dl (IP, full UA,
 * E-SIGN footer) lives behind a "Details" toggle so the post-
 * acceptance page surfaces project work, not a wall of audit data.
 * Tier-1 visibility for the celebratory "yes, this is signed"
 * affordance; the legal record is one click away. */
function AcceptanceAudit({ quote }: { quote: QuoteResponse }) {
  const [showDetails, setShowDetails] = useState(false);

  if (quote.status === "accepted") {
    return (
      <section className="rounded-lg border border-pencil-yellow/40 bg-pencil-yellow-soft px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-graphite" aria-hidden />
            <p className="font-mono text-sm text-graphite">
              Signed by{" "}
              <span className="font-semibold">{quote.signed_name ?? "—"}</span>
              {quote.accepted_at && (
                <>
                  {" "}
                  <span className="text-graphite-soft">
                    · {formatTime(quote.accepted_at)}
                  </span>
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft hover:text-graphite"
          >
            {showDetails ? "Hide details" : "Audit details"}
          </button>
        </div>

        {showDetails && (
          <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 border-t border-pencil-yellow/40 pt-3 text-sm sm:grid-cols-[120px_1fr]">
            <dt className="font-mono text-xs uppercase tracking-wider text-graphite-soft">
              IP
            </dt>
            <dd className="font-mono text-xs text-graphite">
              {quote.signed_ip ?? "—"}
            </dd>

            <dt className="font-mono text-xs uppercase tracking-wider text-graphite-soft">
              Browser
            </dt>
            <dd className="font-mono text-xs text-graphite">
              {parseUserAgent(quote.signed_user_agent)}
              {quote.signed_user_agent && (
                <span className="ml-2 break-all text-[10px] text-graphite-faint">
                  ({quote.signed_user_agent})
                </span>
              )}
            </dd>
          </dl>
        )}
        {showDetails && (
          <p className="mt-2 font-mono text-[10px] text-graphite-soft">
            Typed-name + checkbox + IP + user-agent + timestamp = binding
            e-signature record under the US E-SIGN Act.
          </p>
        )}
      </section>
    );
  }

  // Declined
  return (
    <section className="rounded-lg border border-red-pencil/40 bg-red-pencil/5 px-4 py-3">
      <div className="flex items-center gap-2">
        <ThumbsDown className="h-4 w-4 text-red-pencil" aria-hidden />
        <p className="font-mono text-sm text-graphite">
          Declined{" "}
          <span className="text-graphite-soft">
            {quote.declined_at ? `· ${formatTime(quote.declined_at)}` : ""}
          </span>
        </p>
      </div>
      {quote.decline_reason && (
        <p className="mt-2 border-l-2 border-red-pencil/50 pl-3 text-sm italic text-graphite-soft">
          "{quote.decline_reason}"
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------
// DepositReceivedDialog — modal for "Mark deposit received."
// Pre-fills the amount at 30% of the quote total (typical
// remodel deposit). Captures an internal payment-method note.
// ---------------------------------------------------------------

function DepositReceivedDialog({
  totalCents,
  submitting,
  error,
  onCancel,
  onSubmit,
}: {
  totalCents: number;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: { amount_cents: number; note: string | null }) => void;
}) {
  const [amountText, setAmountText] = useState<string>(
    (totalCents > 0 ? Math.round(totalCents * 0.3) / 100 : 0).toFixed(2),
  );
  const [note, setNote] = useState<string>("");

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-graphite/40 p-6">
      <div className="w-full max-w-md rounded-lg border border-rule bg-paper p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-mono text-base font-bold tracking-tight">Mark deposit received</h2>
            <p className="mt-1 text-xs text-graphite-soft">
              Off-platform receipt. Visible to the homeowner on the project portal once saved; the
              note stays internal.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-graphite-soft hover:text-graphite"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            const cents = Math.round(Number(amountText || 0) * 100);
            if (Number.isNaN(cents) || cents < 0) return;
            onSubmit({
              amount_cents: cents,
              note: note.trim() ? note.trim() : null,
            });
          }}
        >
          <div>
            <label
              htmlFor="deposit-amount"
              className="block font-mono text-[11px] font-semibold uppercase tracking-wider text-graphite-soft"
            >
              Amount received
            </label>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-sm text-graphite-soft">$</span>
              <input
                id="deposit-amount"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
                className="w-32 rounded border border-rule bg-paper-warm px-3 py-1.5 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
              />
              <span className="font-mono text-[11px] text-graphite-faint">
                Pre-filled at 30% of total.
              </span>
            </div>
          </div>

          <div>
            <label
              htmlFor="deposit-note"
              className="block font-mono text-[11px] font-semibold uppercase tracking-wider text-graphite-soft"
            >
              Note (internal)
            </label>
            <input
              id="deposit-note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="Check #1234 · Stripe link · Venmo"
              className="mt-1 w-full rounded border border-rule bg-paper-warm px-3 py-1.5 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
            />
            <p className="mt-1 font-mono text-[11px] text-graphite-faint">
              Visible to your team only. Never shown on the homeowner's quote.
            </p>
          </div>

          {error && (
            <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft hover:text-graphite"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
            >
              <DollarSign className="h-3.5 w-3.5" aria-hidden />
              {submitting ? "Saving…" : "Save deposit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// PromoteAttachmentDialog — confirm + label step before copying a
// comment-attachment onto the parent job's curated attachments.
// Default label seeds from the original filename minus its
// extension; contractor can rewrite freely. ``label`` is required
// server-side (1-255 chars).
// ---------------------------------------------------------------

function PromoteAttachmentDialog({
  attachment,
  submitting,
  error,
  onCancel,
  onSubmit,
}: {
  attachment: QuoteCommentAttachmentResponse;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (label: string) => void;
}) {
  const [label, setLabel] = useState(() => filenameWithoutExtension(attachment.original_filename));
  const labelInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Mirror autoFocus without tripping biome's a11y rule. Modals
    // are an opt-in focus-stealing surface; landing the cursor in
    // the only required field on open is the right UX.
    labelInputRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !submitting) onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [submitting, onCancel]);

  const trimmed = label.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= 255 && !submitting;

  return (
    // biome-ignore lint/a11y/useSemanticElements: matches the ConfirmDialog modal pattern; native <dialog> has different lifecycle requirements this component doesn't model.
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="promote-attachment-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-graphite/40 px-6 py-12"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape" && !submitting) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-rule bg-paper-warm shadow-lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) onSubmit(trimmed);
          }}
        >
          <div className="flex items-start gap-3 px-5 py-4">
            <div className="flex-1">
              <h2
                id="promote-attachment-title"
                className="font-mono text-base font-bold tracking-tight text-graphite"
              >
                Send to job?
              </h2>
              <p className="mt-1 text-sm text-graphite-soft">
                Copies{" "}
                <span className="font-medium text-graphite">{attachment.original_filename}</span>{" "}
                onto the parent job's curated attachments. The original stays in the conversation.
              </p>
              <label
                htmlFor="promote-label"
                className="mt-3 block font-mono text-[11px] font-semibold uppercase tracking-wider text-graphite-soft"
              >
                Label
              </label>
              <input
                ref={labelInputRef}
                id="promote-label"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={255}
                placeholder="What this is — kitchen demo notes, signed scope, etc."
                className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
              />
              {error && <p className="mt-2 text-sm text-red-pencil">{error}</p>}
            </div>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              aria-label="Cancel"
              className="text-graphite-soft hover:text-graphite disabled:opacity-50"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <div className="flex justify-end gap-2 border-t border-rule bg-paper px-5 py-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft transition hover:text-graphite disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" aria-hidden />
              {submitting ? "Sending…" : "Send to job"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// SendToJobToast — confirmation chip after a successful promote.
// Auto-dismisses after 4s; ``X`` to close earlier.
// ---------------------------------------------------------------

function SendToJobToast({
  label,
  onDismiss,
}: {
  label: string;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const t = window.setTimeout(onDismiss, 4000);
    return () => window.clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="rounded-lg border border-pencil-yellow/60 bg-pencil-yellow-soft p-3">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-graphite" aria-hidden />
        <p className="flex-1 text-sm text-graphite">
          <span className="font-medium">Sent to job</span> as{" "}
          <span className="font-mono">"{label}"</span>.
        </p>
        <button
          type="button"
          onClick={onDismiss}
          className="font-mono text-[10px] text-graphite-soft hover:text-graphite"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
